<template>
  <div
    class="search-page-header-input"
    :class="{ 'search-page-header-input--open': showDropdown }"
  >
    <div class="app-header__search-page-input-wrap search-page-header-input__wrap">
      <v-icon size="20" class="app-header__search-page-icon">mdi-magnify</v-icon>
      <input
        ref="inputRef"
        v-model="localQuery"
        type="text"
        class="app-header__search-page-input"
        enterkeyhint="search"
        autocomplete="off"
        autocapitalize="off"
        placeholder="Search posts, users, hashtags..."
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.down.prevent="onArrow(1)"
        @keydown.up.prevent="onArrow(-1)"
        @keydown.enter="onEnter"
        @keydown.escape="onEscape"
      />
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        size="16"
        width="2"
        class="search-page-header-input__spinner"
      />
      <button
        v-else-if="localQuery"
        type="button"
        class="app-header__search-page-clear"
        aria-label="Clear"
        @click="onClear"
      >
        <v-icon size="18">mdi-close-circle</v-icon>
      </button>
    </div>

    <Transition name="search-page-header-input-dd">
      <div
        v-show="showDropdown"
        class="search-page-header-input__dropdown"
        role="listbox"
        :aria-label="'Search suggestions'"
      >
        <div
          v-if="
            isLoading &&
            userResults.length === 0 &&
            hashtagResults.length === 0
          "
          class="search-page-header-input__loading"
        >
          <v-progress-circular indeterminate size="18" width="2" />
          <span>Searching…</span>
        </div>

        <div
          v-if="userResults.length > 0"
          class="search-page-header-input__section"
        >
          <div class="search-page-header-input__section-label">People</div>
          <button
            v-for="(u, i) in userResults"
            :key="u.ulid"
            type="button"
            class="search-page-header-input__item"
            :class="{
              'search-page-header-input__item--active': flatIndex === i,
            }"
            role="option"
            @mousedown.prevent="selectUser(u)"
          >
            <UserAvatar
              class="search-page-header-input__avatar"
              :avatar="userAvatarSrc(u)"
              :avatar-variants="userAvatarVariants(u)"
              :size="36"
              borderless
            />
            <div class="search-page-header-input__item-text">
              <span class="search-page-header-input__item-title">{{
                userDisplayName(u)
              }}</span>
              <span class="search-page-header-input__item-sub"
                >@{{ u.username }}</span
              >
            </div>
          </button>
        </div>

        <div
          v-if="hashtagResults.length > 0"
          class="search-page-header-input__section"
        >
          <div class="search-page-header-input__section-label">Tags</div>
          <button
            v-for="(h, j) in hashtagResults"
            :key="h.name"
            type="button"
            class="search-page-header-input__item search-page-header-input__item--tag"
            :class="{
              'search-page-header-input__item--active':
                flatIndex === userResults.length + j,
            }"
            role="option"
            @mousedown.prevent="selectHashtag(h.name)"
          >
            <v-icon size="20" class="search-page-header-input__tag-icon"
              >mdi-pound</v-icon
            >
            <span class="search-page-header-input__item-title"
              >{{ h.name }}</span
            >
          </button>
        </div>

        <button
          v-if="showQueryRow"
          type="button"
          class="search-page-header-input__item search-page-header-input__item--query"
          :class="{
            'search-page-header-input__item--active':
              flatIndex === flatLength - 1,
          }"
          role="option"
          @mousedown.prevent="applyFullSearch"
        >
          <v-icon size="20" class="search-page-header-input__query-icon"
            >mdi-magnify</v-icon
          >
          <div class="search-page-header-input__item-text">
            <span class="search-page-header-input__item-title"
              >Search for "{{ localQuery.trim() }}"</span
            >
            <span class="search-page-header-input__item-sub"
              >Posts, people, and more</span
            >
          </div>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUiStore } from "@/stores/ui";
import { debounce } from "@/utils/debounce";
import UserAvatar from "@/components/common/UserAvatar.vue";
import {
  useSearchPageAutocomplete,
  SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH,
} from "@/composables/useSearchPageAutocomplete";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";
import type { ImageVariants } from "@/types";

function userAvatarSrc(u: UserProfile): string {
  const r = u as Record<string, unknown>;
  const url = (r.avatarUrl || r.avatar) as string | undefined;
  return typeof url === "string" ? url : "";
}

function userAvatarVariants(u: UserProfile): ImageVariants | null {
  const r = u as Record<string, unknown>;
  const v = r.avatarVariants;
  if (v && typeof v === "object") return v as ImageVariants;
  return null;
}

function userDisplayName(u: UserProfile): string {
  const r = u as Record<string, unknown>;
  const name = (r.name || r.displayName || r.username) as string | undefined;
  return name?.trim() || u.username || "User";
}

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const inputRef = ref<HTMLInputElement | null>(null);

const localQuery = ref("");
const isFocused = ref(false);
const flatIndex = ref(-1);

const { userResults, hashtagResults, isLoading, debouncedFetch, fetchNow } =
  useSearchPageAutocomplete({
    getQuery: () => localQuery.value,
  });

const showQueryRow = computed(() => {
  const q = localQuery.value.trim();
  if (q.length < SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH) return false;
  if (q.startsWith("#") || q.startsWith("@")) return false;
  return true;
});

const flatLength = computed(() => {
  let n = userResults.value.length + hashtagResults.value.length;
  if (showQueryRow.value) n += 1;
  return n;
});

const showDropdown = computed(() => {
  if (!isFocused.value) return false;
  const q = localQuery.value.trim();
  if (q.length < SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH) return false;
  return (
    userResults.value.length > 0 ||
    hashtagResults.value.length > 0 ||
    showQueryRow.value ||
    isLoading.value
  );
});

let blurHideTimer: ReturnType<typeof setTimeout> | null = null;

function clearBlurTimer() {
  if (blurHideTimer) {
    clearTimeout(blurHideTimer);
    blurHideTimer = null;
  }
}

/** Updates `?q=` + tab strip; SearchView watch runs `performSearch`. */
function commitSearchToRoute(trimmed: string) {
  const current = typeof route.query.q === "string" ? route.query.q : "";
  if (current === trimmed) {
    if (trimmed) uiStore.setSearchShowCategoryTabs(true);
    return;
  }
  router.replace({
    path: "/search",
    query: trimmed ? { q: trimmed } : {},
  });
  if (trimmed) uiStore.setSearchShowCategoryTabs(true);
  else uiStore.setSearchShowCategoryTabs(false);
}

/** Debounced so typing drives full search without Enter; keeps focus for quick-search dropdown. */
const debouncedSyncRoute = debounce(() => {
  commitSearchToRoute(localQuery.value.trim());
}, 400);

function syncFromRoute() {
  const q = route.query.q;
  const s = typeof q === "string" ? q : "";
  if (s !== localQuery.value) localQuery.value = s;
}

watch(
  () => route.query.q,
  () => {
    debouncedSyncRoute.cancel();
    syncFromRoute();
  },
  { immediate: true },
);

function onInput() {
  flatIndex.value = -1;
  debouncedFetch();
  debouncedSyncRoute();
}

function onFocus() {
  clearBlurTimer();
  isFocused.value = true;
  if (localQuery.value.trim().length >= SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH) {
    void fetchNow();
  }
}

function onBlur() {
  clearBlurTimer();
  blurHideTimer = setTimeout(() => {
    isFocused.value = false;
    flatIndex.value = -1;
    blurHideTimer = null;
  }, 200);
}

function onClear() {
  debouncedSyncRoute.cancel();
  localQuery.value = "";
  flatIndex.value = -1;
  userResults.value = [];
  hashtagResults.value = [];
  uiStore.setSearchShowCategoryTabs(false);
  router.replace({ path: "/search", query: {} });
  inputRef.value?.focus();
}

/** Full search from Enter / “Search for…” row — commit route and dismiss quick-search (blur). */
function applyFullSearch() {
  const trimmed = localQuery.value.trim();
  if (!trimmed) return;
  clearBlurTimer();
  debouncedSyncRoute.cancel();
  flatIndex.value = -1;
  commitSearchToRoute(trimmed);
  void fetchNow();
  isFocused.value = false;
  inputRef.value?.blur();
}

function selectUser(user: UserProfile) {
  debouncedSyncRoute.cancel();
  clearBlurTimer();
  isFocused.value = false;
  flatIndex.value = -1;
  const u = user.username;
  if (u) router.push(`/profile/${encodeURIComponent(u)}`);
}

function selectHashtag(name: string) {
  debouncedSyncRoute.cancel();
  clearBlurTimer();
  isFocused.value = false;
  flatIndex.value = -1;
  localQuery.value = `#${name}`;
  commitSearchToRoute(`#${name}`);
}

function flatPick(index: number) {
  const nu = userResults.value.length;
  const nh = hashtagResults.value.length;
  if (index < nu) {
    selectUser(userResults.value[index]!);
    return;
  }
  if (index < nu + nh) {
    selectHashtag(hashtagResults.value[index - nu]!.name);
    return;
  }
  if (showQueryRow.value && index === nu + nh) {
    applyFullSearch();
  }
}

function onArrow(dir: 1 | -1) {
  if (!showDropdown.value || flatLength.value === 0) return;
  let next = flatIndex.value + dir;
  if (next < 0) next = flatLength.value - 1;
  else if (next >= flatLength.value) next = -1;
  flatIndex.value = next;
}

function onEnter() {
  if (flatIndex.value >= 0 && flatIndex.value < flatLength.value) {
    flatPick(flatIndex.value);
    return;
  }
  applyFullSearch();
}

function onEscape() {
  clearBlurTimer();
  isFocused.value = false;
  flatIndex.value = -1;
}

onUnmounted(() => {
  debouncedFetch.cancel();
  debouncedSyncRoute.cancel();
  clearBlurTimer();
});

defineExpose({
  focus: () => inputRef.value?.focus(),
});
</script>

<style scoped lang="scss">
.search-page-header-input {
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;

  &__wrap {
    position: relative;
    z-index: 1;
  }

  &__spinner {
    flex-shrink: 0;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.55);
  }

  &__dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    z-index: var(--z-sticky-content);
    max-height: min(70vh, 420px);
    overflow-y: auto;
    padding: 8px 0;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  }

  &__section {
    padding: 4px 0;

    &:not(:first-child) {
      border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
      margin-top: 4px;
      padding-top: 10px;
    }
  }

  &__section-label {
    padding: 4px 16px 8px;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), 0.45);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 16px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;

    &:hover,
    &--active {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }

    &--active {
      background: rgba(245, 158, 11, 0.12);
    }

    &--tag {
      gap: 10px;
    }

    &--query {
      border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
      margin-top: 4px;
      padding-top: 12px;
    }
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__tag-icon,
  &__query-icon {
    flex-shrink: 0;
    color: rgba(var(--v-theme-on-surface), 0.55);
  }

  &__item-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-sub {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.55);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.search-page-header-input-dd-enter-active,
.search-page-header-input-dd-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.search-page-header-input-dd-enter-from,
.search-page-header-input-dd-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
